/**
 * InvoiceInsights — three data-bound context cards above the table.
 *
 * The prototype copy is static. This component keeps the same visual role
 * while deriving every number from `invoices.list`, so the screen stays
 * honest until richer receipts and audit-log analytics land.
 *
 * @see prototype/sales/invoices.html — insight cards
 */
import { Clock, MessageCircle, TrendingUp } from "lucide-react";
import type { ReactNode } from "react";
import { formatLakhCrore, parsePaise } from "../lib/format-money.ts";
import type { InvoiceTableRow } from "./invoice-table.tsx";

export interface InvoiceInsightsProps {
  rows: InvoiceTableRow[];
  kpis: {
    paidThisMonthPaise: string;
    paidLastMonthPaise: string;
    outstanding7dDeltaPaise: string;
    overdueInvoiceCount: number;
    oldestOverdueDays: number | null;
  };
}

export function InvoiceInsights({ rows, kpis }: InvoiceInsightsProps) {
  const stats = buildInsightStats(rows);
  const paidDelta = parsePaise(kpis.paidThisMonthPaise) - parsePaise(kpis.paidLastMonthPaise);
  const outstandingDelta = BigInt(kpis.outstanding7dDeltaPaise || "0");

  return (
    <section className="grid grid-cols-1 gap-3 lg:grid-cols-3">
      <InsightCard
        tone="emerald"
        icon={<TrendingUp size={16} aria-hidden />}
        headline={
          paidDelta > 0n
            ? `+${formatLakhCrore(paidDelta.toString())} collected`
            : `${formatLakhCrore(kpis.paidThisMonthPaise)} collected`
        }
        body={
          kpis.paidLastMonthPaise === "0"
            ? "Cash received this month. Last-month comparison will appear after receipts history grows."
            : `${formatLakhCrore(kpis.paidThisMonthPaise)} this month vs ${formatLakhCrore(kpis.paidLastMonthPaise)} last month.`
        }
      />
      <InsightCard
        icon={<Clock size={16} aria-hidden />}
        headline={`Avg invoice ${formatLakhCrore(stats.avgPaise.toString())}`}
        body={
          stats.largest
            ? `Largest visible invoice is ${formatLakhCrore(stats.largest.totalPaise.toString())} from ${stats.largest.customerName}.`
            : "Visible invoice averages update as filters change."
        }
      />
      <InsightCard
        tone="amber"
        icon={<MessageCircle size={16} aria-hidden />}
        headline={`${kpis.overdueInvoiceCount} reminder${kpis.overdueInvoiceCount === 1 ? "" : "s"} ready`}
        body={
          kpis.overdueInvoiceCount === 0
            ? "No overdue invoices in this view. Keep reminders off unless a due date slips."
            : `Oldest overdue is ${kpis.oldestOverdueDays ?? 0} days. Outstanding moved ${formatSignedPaise(outstandingDelta)} over the last 7 days.`
        }
      />
    </section>
  );
}

interface InsightCardProps {
  icon: ReactNode;
  headline: string;
  body: string;
  tone?: "default" | "emerald" | "amber";
}

const TONE_CLASS: Record<NonNullable<InsightCardProps["tone"]>, string> = {
  default: "bg-sky-50 text-sky-700",
  emerald: "bg-emerald-50 text-emerald-700",
  amber: "bg-amber-50 text-amber-700",
};

function InsightCard({ icon, headline, body, tone = "default" }: InsightCardProps) {
  return (
    <button
      type="button"
      className="flex items-start gap-3 rounded-md border border-line bg-paper p-4 text-left shadow-sm transition hover:-translate-y-px hover:border-line-2 hover:shadow-md"
    >
      <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-md ${TONE_CLASS[tone]}`}>
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-base font-semibold leading-tight text-ink">{headline}</span>
        <span className="mt-1 block text-sm leading-snug text-muted">{body}</span>
      </span>
    </button>
  );
}

function buildInsightStats(rows: InvoiceTableRow[]): {
  avgPaise: bigint;
  largest: { totalPaise: bigint; customerName: string } | null;
} {
  if (rows.length === 0) return { avgPaise: 0n, largest: null };
  let sum = 0n;
  let largest: { totalPaise: bigint; customerName: string } | null = null;
  for (const row of rows) {
    // Net of credit notes — matches the Amount column and the table-
    // footer's "Page total / Avg invoice / Largest" stats so the two
    // cards never disagree on the same data.
    const amount = parsePaise(row.totalPaise) - parsePaise(row.creditedPaise);
    sum += amount;
    if (!largest || amount > largest.totalPaise) {
      largest = { totalPaise: amount, customerName: row.customerName };
    }
  }
  return { avgPaise: sum / BigInt(rows.length), largest };
}

function formatSignedPaise(value: bigint): string {
  if (value === 0n) return formatLakhCrore("0");
  const abs = value < 0n ? -value : value;
  return `${value > 0n ? "+" : "-"}${formatLakhCrore(abs.toString())}`;
}
